import { z } from 'zod';
import { EKHO_SERVICES_PROVIDER_ID } from '../constants.js';
import { Notification } from './notifications/Notification.js';
import { NotificationEvent } from './notifications/NotificationEvent.js';
import { NotificationSubscriberType } from './notifications/NotificationSubscription.js';
import { NOTIFICATION_TEMPLATES_BY_EVENT, interpolateTemplate } from './notifications/NotificationTemplate.js';
import { RiskReviewStatus } from './Sardine.js';

// ============================================================================
// Enums
// ============================================================================
// Preorder Action Item Codes
const PreorderActionItemCode = {
    PAY_DEPOSIT: 'PAY_DEPOSIT',
    RECEIVE_DEPOSIT: 'RECEIVE_DEPOSIT',
    INVITE_TO_CONVERT_TO_FULL_ORDER: 'INVITE_TO_CONVERT_TO_FULL_ORDER',
    CONVERT_TO_FULL_ORDER: 'CONVERT_TO_FULL_ORDER',
};
const preorderActionItemCodeValues = [
    PreorderActionItemCode.PAY_DEPOSIT,
    PreorderActionItemCode.RECEIVE_DEPOSIT,
    PreorderActionItemCode.INVITE_TO_CONVERT_TO_FULL_ORDER,
    PreorderActionItemCode.CONVERT_TO_FULL_ORDER,
];
const PreorderActionItemCodeSchema = z.enum(preorderActionItemCodeValues);
// Payment Action Item Codes
const PaymentActionItemCode = {
    PAY_IN_FULL: 'PAY_IN_FULL',
    PAY_RESERVATION_FEE: 'PAY_RESERVATION_FEE',
    PAY_RESERVATION_BALANCE_EARLY: 'PAY_RESERVATION_BALANCE_EARLY',
    PAY_RESERVATION_BALANCE_LATE: 'PAY_RESERVATION_BALANCE_LATE',
    RECEIVE_FULL_KLARNA_PAYMENT: 'RECEIVE_FULL_KLARNA_PAYMENT',
    RECEIVE_FULL_CRYPTO_PAYMENT: 'RECEIVE_FULL_CRYPTO_PAYMENT',
    RECEIVE_FULL_PAYMENT: 'RECEIVE_FULL_PAYMENT',
    RECEIVE_FULL_PAYMENT_ACH: 'RECEIVE_FULL_PAYMENT_ACH',
    RECEIVE_RESERVATION_FEE: 'RECEIVE_RESERVATION_FEE',
    RECEIVE_RESERVATION_BALANCE_EARLY: 'RECEIVE_RESERVATION_BALANCE_EARLY',
    RECEIVE_RESERVATION_BALANCE_LATE: 'RECEIVE_RESERVATION_BALANCE_LATE',
    SIGN_PURCHASE_AGREEMENT: 'SIGN_PURCHASE_AGREEMENT',
    COUNTER_SIGN_PURCHASE_AGREEMENT: 'COUNTER_SIGN_PURCHASE_AGREEMENT',
    SUBMIT_DISPUTE_EVIDENCE: 'SUBMIT_DISPUTE_EVIDENCE',
    SIGN_SALE_DOCUMENT: 'SIGN_SALE_DOCUMENT',
    COUNTER_SIGN_SALE_DOCUMENT: 'COUNTER_SIGN_SALE_DOCUMENT',
    RELEASE_INVENTORY_PAYMENT: 'RELEASE_INVENTORY_PAYMENT',
};
const paymentActionItemCodeValues = [
    PaymentActionItemCode.PAY_RESERVATION_FEE,
    PaymentActionItemCode.PAY_RESERVATION_BALANCE_EARLY,
    PaymentActionItemCode.PAY_RESERVATION_BALANCE_LATE,
    PaymentActionItemCode.PAY_IN_FULL,
    PaymentActionItemCode.RECEIVE_FULL_KLARNA_PAYMENT,
    PaymentActionItemCode.RECEIVE_FULL_CRYPTO_PAYMENT,
    PaymentActionItemCode.RECEIVE_FULL_PAYMENT,
    PaymentActionItemCode.RECEIVE_FULL_PAYMENT_ACH,
    PaymentActionItemCode.RECEIVE_RESERVATION_FEE,
    PaymentActionItemCode.RECEIVE_RESERVATION_BALANCE_EARLY,
    PaymentActionItemCode.RECEIVE_RESERVATION_BALANCE_LATE,
    PaymentActionItemCode.SIGN_PURCHASE_AGREEMENT,
    PaymentActionItemCode.COUNTER_SIGN_PURCHASE_AGREEMENT,
    PaymentActionItemCode.SUBMIT_DISPUTE_EVIDENCE,
    PaymentActionItemCode.SIGN_SALE_DOCUMENT,
    PaymentActionItemCode.COUNTER_SIGN_SALE_DOCUMENT,
    PaymentActionItemCode.RELEASE_INVENTORY_PAYMENT,
];
const PaymentActionItemCodeSchema = z.enum(paymentActionItemCodeValues);
// Security Action Item Codes
const SecurityActionItemCode = {
    REVIEW_RISK_ASSESSMENT: 'REVIEW_RISK_ASSESSMENT',
    SUBMIT_IDENTITY_VERIFICATION: 'SUBMIT_IDENTITY_VERIFICATION',
    REVIEW_IDENTITY_VERIFICATION: 'REVIEW_IDENTITY_VERIFICATION',
    CHECK_OFAC: 'CHECK_OFAC',
};
const securityActionItemCodeValues = [
    SecurityActionItemCode.REVIEW_RISK_ASSESSMENT,
    SecurityActionItemCode.SUBMIT_IDENTITY_VERIFICATION,
    SecurityActionItemCode.REVIEW_IDENTITY_VERIFICATION,
    SecurityActionItemCode.CHECK_OFAC,
];
const SecurityActionItemCodeSchema = z.enum(securityActionItemCodeValues);
// Fulfillment Action Item Codes
const FulfillmentActionItemCode = {
    GET_SOFT_LOCK_READY_TO_UPGRADE: 'GET_SOFT_LOCK_READY_TO_UPGRADE',
    MARK_BACKFILL_INVENTORY_REQUESTED: 'MARK_BACKFILL_INVENTORY_REQUESTED',
    MARK_BACKFILL_INVENTORY_ADDED_TO_INVENTORY_SYSTEM: 'MARK_BACKFILL_INVENTORY_ADDED_TO_INVENTORY_SYSTEM',
    MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER: 'MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER',
    MARK_RECEIVED_AT_FULFILLMENT_CENTER: 'MARK_RECEIVED_AT_FULFILLMENT_CENTER',
    MARK_IN_TRANSIT_TO_BUYER: 'MARK_IN_TRANSIT_TO_BUYER',
    MARK_DELIVERY_FULFILLED: 'MARK_DELIVERY_FULFILLED',
    MARK_PICKUP_FULFILLED: 'MARK_PICKUP_FULFILLED',
    MARK_ASSEMBLED: 'MARK_ASSEMBLED',
    MARK_INSPECTED: 'MARK_INSPECTED',
    MARK_PICKUP_SCHEDULED: 'MARK_PICKUP_SCHEDULED',
    MARK_FULFILLED: 'MARK_FULFILLED',
    MARK_BACKFILL_ACCESSORIES_REQUESTED: 'MARK_BACKFILL_ACCESSORIES_REQUESTED',
};
const fulfillmentActionItemCodeValues = [
    FulfillmentActionItemCode.GET_SOFT_LOCK_READY_TO_UPGRADE,
    FulfillmentActionItemCode.MARK_BACKFILL_INVENTORY_REQUESTED,
    FulfillmentActionItemCode.MARK_BACKFILL_INVENTORY_ADDED_TO_INVENTORY_SYSTEM,
    FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER,
    FulfillmentActionItemCode.MARK_RECEIVED_AT_FULFILLMENT_CENTER,
    FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_BUYER,
    FulfillmentActionItemCode.MARK_DELIVERY_FULFILLED,
    FulfillmentActionItemCode.MARK_PICKUP_FULFILLED,
    FulfillmentActionItemCode.MARK_ASSEMBLED,
    FulfillmentActionItemCode.MARK_INSPECTED,
    FulfillmentActionItemCode.MARK_PICKUP_SCHEDULED,
    FulfillmentActionItemCode.MARK_FULFILLED,
    FulfillmentActionItemCode.MARK_BACKFILL_ACCESSORIES_REQUESTED,
];
const FulfillmentActionItemCodeSchema = z.enum(fulfillmentActionItemCodeValues);
// Financing Action Item Codes
const FinancingActionItemCode = {
    SUBMIT_FINANCING_APPLICATION_TO_EKHO: 'SUBMIT_FINANCING_APPLICATION_TO_EKHO',
    SUBMIT_FINANCING_APPLICATION_TO_LENDING_PARTNERS: 'SUBMIT_FINANCING_APPLICATION_TO_LENDING_PARTNERS',
    DECLINE_FINANCING_OFFER: 'DECLINE_FINANCING_OFFER',
    SUBMIT_STIPULATION_EVIDENCE: 'SUBMIT_STIPULATION_EVIDENCE',
    SWITCH_TO_CASH: 'SWITCH_TO_CASH',
    RECEIVE_SWITCH_TO_CASH: 'RECEIVE_SWITCH_TO_CASH',
    CANCEL_ORDER_AFTER_FINAL_REJECTION: 'CANCEL_ORDER_AFTER_FINAL_REJECTION',
    PAY_DOWN_PAYMENT: 'PAY_DOWN_PAYMENT',
    RECEIVE_DOWN_PAYMENT: 'RECEIVE_DOWN_PAYMENT',
    SIGN_LOAN_CONTRACT: 'SIGN_LOAN_CONTRACT',
    SIGN_FINANCING_DOCUMENTS: 'SIGN_FINANCING_DOCUMENTS',
    VALIDATE_STIPULATION_EVIDENCE: 'VALIDATE_STIPULATION_EVIDENCE',
    COUNTER_SIGN_LOAN_CONTRACT: 'COUNTER_SIGN_LOAN_CONTRACT',
    SUBMIT_FUNDING_PACKET: 'SUBMIT_FUNDING_PACKET',
    RECEIVE_LENDER_FUNDS: 'RECEIVE_LENDER_FUNDS',
    REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW: 'REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW',
    REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED: 'REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED',
    REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED: 'REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED',
    REVIEW_SURFACED_LENDER_DECISION_APPROVED: 'REVIEW_SURFACED_LENDER_DECISION_APPROVED',
    REVIEW_SURFACED_LENDER_DECISION_REJECTED: 'REVIEW_SURFACED_LENDER_DECISION_REJECTED',
    FINANCING_OFFER_EXPIRATION_ALERT: 'FINANCING_OFFER_EXPIRATION_ALERT',
};
const financingActionItemCodeValues = [
    FinancingActionItemCode.SUBMIT_FINANCING_APPLICATION_TO_EKHO,
    FinancingActionItemCode.SUBMIT_FINANCING_APPLICATION_TO_LENDING_PARTNERS,
    FinancingActionItemCode.DECLINE_FINANCING_OFFER,
    FinancingActionItemCode.SUBMIT_STIPULATION_EVIDENCE,
    FinancingActionItemCode.VALIDATE_STIPULATION_EVIDENCE,
    FinancingActionItemCode.SWITCH_TO_CASH,
    FinancingActionItemCode.RECEIVE_SWITCH_TO_CASH,
    FinancingActionItemCode.CANCEL_ORDER_AFTER_FINAL_REJECTION,
    FinancingActionItemCode.PAY_DOWN_PAYMENT,
    FinancingActionItemCode.RECEIVE_DOWN_PAYMENT,
    FinancingActionItemCode.SIGN_LOAN_CONTRACT,
    FinancingActionItemCode.SIGN_FINANCING_DOCUMENTS,
    FinancingActionItemCode.VALIDATE_STIPULATION_EVIDENCE,
    FinancingActionItemCode.COUNTER_SIGN_LOAN_CONTRACT,
    FinancingActionItemCode.SUBMIT_FUNDING_PACKET,
    FinancingActionItemCode.RECEIVE_LENDER_FUNDS,
    FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW,
    FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED,
    FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED,
    FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_APPROVED,
    FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_REJECTED,
    FinancingActionItemCode.FINANCING_OFFER_EXPIRATION_ALERT,
];
const FinancingActionItemCodeSchema = z.enum(financingActionItemCodeValues);
// Insurance Action Item Codes
const InsuranceActionItemCode = {
    SUBMIT_INSURANCE_INFORMATION: 'SUBMIT_INSURANCE_INFORMATION',
    REVIEW_INSURANCE_INFORMATION: 'REVIEW_INSURANCE_INFORMATION',
};
const insuranceActionItemCodeValues = [
    InsuranceActionItemCode.SUBMIT_INSURANCE_INFORMATION,
    InsuranceActionItemCode.REVIEW_INSURANCE_INFORMATION,
];
const InsuranceActionItemCodeSchema = z.enum(insuranceActionItemCodeValues);
// Tar Action Item Codes
const TarActionItemCode = {
    SUBMIT_BUYER_UPLOADS: 'SUBMIT_BUYER_UPLOADS',
    SUBMIT_OWNER_DETAILS: 'SUBMIT_OWNER_DETAILS',
    ASSIGN_VIN: 'ASSIGN_VIN',
    ASSIGN_ODOMETER_READING: 'ASSIGN_ODOMETER_READING',
    ASSIGN_MOTORCYCLE_ENGINE_NUMBER: 'ASSIGN_MOTORCYCLE_ENGINE_NUMBER',
    MARK_PRODUCTION_COMPLETE: 'MARK_PRODUCTION_COMPLETE',
    CREATE_TAR_TRANSACTION: 'CREATE_TAR_TRANSACTION',
    CREATE_TAR_PAPERWORK: 'CREATE_TAR_PAPERWORK',
    ESIGN_TAR_PAPERWORK: 'ESIGN_TAR_PAPERWORK',
    SCHEDULE_ROBOSIGN_SESSION: 'SCHEDULE_ROBOSIGN_SESSION',
    APPROVE_ROBOSIGN_TAR_PAPERWORK: 'APPROVE_ROBOSIGN_TAR_PAPERWORK',
    MAIL_WETSIG_TAR_PAPERWORK: 'MAIL_WETSIG_TAR_PAPERWORK',
    RECEIVE_WETSIG_TAR_PAPERWORK: 'RECEIVE_WETSIG_TAR_PAPERWORK',
    REVIEW_SIGNED_TAR_PAPERWORK: 'REVIEW_SIGNED_TAR_PAPERWORK',
    PROCESS_SIGNED_TAR_PAPERWORK: 'PROCESS_SIGNED_TAR_PAPERWORK',
    SUBMIT_SIGNED_TAR_PAPERWORK: 'SUBMIT_SIGNED_TAR_PAPERWORK',
    GENERATE_TEMP_TAG: 'GENERATE_TEMP_TAG',
    COUNTER_SIGN_TAR_PAPERWORK: 'COUNTER_SIGN_TAR_PAPERWORK',
    GENERATE_PLATE: 'GENERATE_PLATE',
    COMPLETE_WARRANTY_REGISTRATION: 'COMPLETE_WARRANTY_REGISTRATION',
    COMPLETE_MSO: 'COMPLETE_MSO',
    DESTROY_MSO: 'DESTROY_MSO',
    FINALIZE_DEAL_IN_INVENTORY_SYSTEM: 'FINALIZE_DEAL_IN_INVENTORY_SYSTEM',
    UPLOAD_POST_FULFILLMENT_FORMS: 'UPLOAD_POST_FULFILLMENT_FORMS',
    REUPLOAD_POST_FULFILLMENT_FORM: 'REUPLOAD_POST_FULFILLMENT_FORM',
    VALIDATE_POST_FULFILLMENT_FORM: 'VALIDATE_POST_FULFILLMENT_FORM',
    SHIP_POST_FULFILLMENT_FORMS: 'SHIP_POST_FULFILLMENT_FORMS',
    SHIP_BUYER_PACKET_TO_SELLER: 'SHIP_BUYER_PACKET_TO_SELLER',
    SHIP_SELLER_FORMS_TO_EKHO: 'SHIP_SELLER_FORMS_TO_EKHO',
    SCHEDULE_COUNTER_SIGN_ROBOSIGN_SESSION: 'SCHEDULE_COUNTER_SIGN_ROBOSIGN_SESSION',
    APPROVE_COUNTER_SIGN_ROBOSIGN_TAR_PAPERWORK: 'APPROVE_COUNTER_SIGN_ROBOSIGN_TAR_PAPERWORK',
    SCAN_AND_UPLOAD_FORMS: 'SCAN_AND_UPLOAD_FORMS',
    BUYER_RON_SESSION: 'BUYER_RON_SESSION',
    SELLER_RON_SESSION: 'SELLER_RON_SESSION',
};
const tarActionItemCodeValues = [
    TarActionItemCode.SUBMIT_BUYER_UPLOADS,
    TarActionItemCode.SUBMIT_OWNER_DETAILS,
    TarActionItemCode.ASSIGN_VIN,
    TarActionItemCode.ASSIGN_ODOMETER_READING,
    TarActionItemCode.ASSIGN_MOTORCYCLE_ENGINE_NUMBER,
    TarActionItemCode.MARK_PRODUCTION_COMPLETE,
    TarActionItemCode.CREATE_TAR_TRANSACTION,
    TarActionItemCode.CREATE_TAR_PAPERWORK,
    TarActionItemCode.ESIGN_TAR_PAPERWORK,
    TarActionItemCode.SCHEDULE_ROBOSIGN_SESSION,
    TarActionItemCode.APPROVE_ROBOSIGN_TAR_PAPERWORK,
    TarActionItemCode.MAIL_WETSIG_TAR_PAPERWORK,
    TarActionItemCode.RECEIVE_WETSIG_TAR_PAPERWORK,
    TarActionItemCode.REVIEW_SIGNED_TAR_PAPERWORK,
    TarActionItemCode.PROCESS_SIGNED_TAR_PAPERWORK,
    TarActionItemCode.SUBMIT_SIGNED_TAR_PAPERWORK,
    TarActionItemCode.GENERATE_TEMP_TAG,
    TarActionItemCode.COUNTER_SIGN_TAR_PAPERWORK,
    TarActionItemCode.GENERATE_PLATE,
    TarActionItemCode.COMPLETE_WARRANTY_REGISTRATION,
    TarActionItemCode.COMPLETE_MSO,
    TarActionItemCode.DESTROY_MSO,
    TarActionItemCode.FINALIZE_DEAL_IN_INVENTORY_SYSTEM,
    TarActionItemCode.UPLOAD_POST_FULFILLMENT_FORMS,
    TarActionItemCode.REUPLOAD_POST_FULFILLMENT_FORM,
    TarActionItemCode.VALIDATE_POST_FULFILLMENT_FORM,
    TarActionItemCode.SHIP_POST_FULFILLMENT_FORMS,
    TarActionItemCode.SHIP_BUYER_PACKET_TO_SELLER,
    TarActionItemCode.SHIP_SELLER_FORMS_TO_EKHO,
    TarActionItemCode.APPROVE_COUNTER_SIGN_ROBOSIGN_TAR_PAPERWORK,
    TarActionItemCode.SCHEDULE_COUNTER_SIGN_ROBOSIGN_SESSION,
    TarActionItemCode.SCAN_AND_UPLOAD_FORMS,
    TarActionItemCode.BUYER_RON_SESSION,
    TarActionItemCode.SELLER_RON_SESSION,
];
const TarActionItemCodeSchema = z.enum(tarActionItemCodeValues);
// Action Item Status - (TODO: refactor to follow zod schema paradigm)
var ActionItemStatus;
(function (ActionItemStatus) {
    ActionItemStatus["QUEUED"] = "QUEUED";
    ActionItemStatus["PENDING"] = "PENDING";
    ActionItemStatus["COMPLETE"] = "COMPLETE";
})(ActionItemStatus || (ActionItemStatus = {}));
const actionItemStatusValues = [
    ActionItemStatus.QUEUED,
    ActionItemStatus.PENDING,
    ActionItemStatus.COMPLETE,
];
const ActionItemStatusSchema = z.enum(actionItemStatusValues);
/**
 * Represents the entity responsible for completing a particular action item.
 * Not being used at the moment given codes are prefixed by agent type.
 */
var ActionItemAgent;
(function (ActionItemAgent) {
    ActionItemAgent["BUYER"] = "BUYER";
    ActionItemAgent["EKHO"] = "EKHO";
    ActionItemAgent["OEM"] = "OEM";
    ActionItemAgent["SERVICES_PROVIDER"] = "SERVICES_PROVIDER";
})(ActionItemAgent || (ActionItemAgent = {}));
const actionItemAgentValues = [
    ActionItemAgent.BUYER,
    ActionItemAgent.EKHO,
    ActionItemAgent.OEM,
    ActionItemAgent.SERVICES_PROVIDER,
];
const ActionItemAgentSchema = z.enum(actionItemAgentValues);
/**
 * Represents the category to which a particular action item belongs.
 */
const ActionItemOperation = {
    PREORDER: 'PREORDER',
    PAYMENT: 'PAYMENT',
    SECURITY: 'SECURITY',
    FINANCING: 'FINANCING',
    INSURANCE: 'INSURANCE',
    VEHICLE_PREPARATION: 'VEHICLE_PREPARATION',
    TAR: 'TAR',
    FULFILLMENT: 'FULFILLMENT',
};
// type ActionItemOperation = (typeof ActionItemOperation)[keyof typeof ActionItemOperation];
const actionItemOperationValues = [
    ActionItemOperation.PREORDER,
    ActionItemOperation.PAYMENT,
    ActionItemOperation.SECURITY,
    ActionItemOperation.FINANCING,
    ActionItemOperation.INSURANCE,
    ActionItemOperation.VEHICLE_PREPARATION,
    ActionItemOperation.TAR,
    ActionItemOperation.FULFILLMENT,
];
const ActionItemOperationSchema = z.enum(actionItemOperationValues);
/**
 * Represents an action item that needs to be completed for a purchasedVehicle.
 */
const actionItemCodeValues = [
    ...preorderActionItemCodeValues,
    ...paymentActionItemCodeValues,
    ...securityActionItemCodeValues,
    ...fulfillmentActionItemCodeValues,
    ...financingActionItemCodeValues,
    ...insuranceActionItemCodeValues,
    ...tarActionItemCodeValues,
];
// 3) Tell TS: treat this as a tuple of ActionItemCode values
const actionItemCodeSchema = z.enum(actionItemCodeValues);
class ActionItem {
    constructor(domainsServicesProviderId, programOwnerId, actionCode, operation, agent, status, type, id, config, agentId, service, iterationKey, createdAt, updatedAt, queuedAt, pendingAt, completedAt, metadata) {
        this.domainsServicesProviderId = domainsServicesProviderId;
        this.programOwnerId = programOwnerId;
        this.actionCode = actionCode;
        this.operation = operation;
        this.agent = agent;
        this.status = status;
        this.type = type;
        this.id = id;
        this.config = config;
        this.agentId = agentId;
        this.service = service;
        this.iterationKey = iterationKey;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
        this.queuedAt = queuedAt;
        this.pendingAt = pendingAt;
        this.completedAt = completedAt;
        this.metadata = metadata;
        this.subscriberIdsBySubscriberType = {
            [NotificationSubscriberType.BUYER]: [this.metadata?.buyerId],
            [NotificationSubscriberType.OEM]: [this.metadata?.oemId],
            [NotificationSubscriberType.SERVICES_PROVIDER]: this.metadata?.servicesProviderIds ?? [],
        };
    }
    // For validated, complete data (internal use)
    static fromValidatedData(data) {
        return new ActionItem(data.domainsServicesProviderId, data.metadata?.programOwnerId ?? '', data.actionCode, data.operation, data.agent, data.status, data.type, data.id, data.config, data.agentId, data.service, data.iterationKey, data.createdAt, data.updatedAt, data.queuedAt, data.pendingAt, data.completedAt, data.metadata);
    }
    /**
     * Create an ActionItem from raw data.
     */
    static fromRawData(rawData) {
        // Validate required fields
        if (!rawData.domainsServicesProviderId)
            throw new Error('domainsServicesProviderId is required');
        if (!rawData.programOwnerId)
            throw new Error('programOwnerId is required');
        if (!rawData.actionCode)
            throw new Error('actionCode is required');
        if (!rawData.operation)
            throw new Error('operation is required');
        if (!rawData.agent)
            throw new Error('agent is required');
        if (!rawData.type)
            throw new Error('type is required');
        if (!rawData.config?.title)
            throw new Error('config.title is required');
        if (!rawData.config?.description)
            throw new Error('config.description is required');
        if (!rawData.config?.instructions)
            throw new Error('config.instructions is required');
        const now = Date.now();
        const validatedData = {
            programOwnerId: rawData.programOwnerId,
            domainsServicesProviderId: rawData.domainsServicesProviderId,
            actionCode: rawData.actionCode,
            operation: rawData.operation,
            agent: rawData.agent,
            agentId: rawData.agentId,
            service: rawData.service,
            status: rawData.status || 'pending', // default status
            type: rawData.type,
            config: {
                title: rawData.config.title,
                shortTitle: rawData.config.shortTitle,
                description: rawData.config.description,
                instructions: rawData.config.instructions,
                buttonTitle: rawData.config.buttonTitle,
                actionType: rawData.config.actionType,
            },
            iterationKey: rawData.iterationKey,
            createdAt: rawData.createdAt || now,
            updatedAt: rawData.updatedAt || now,
            queuedAt: rawData.queuedAt ?? null,
            pendingAt: rawData.pendingAt ?? null,
            completedAt: rawData.completedAt ?? null,
            metadata: rawData.metadata,
            id: rawData.id,
        };
        return ActionItem.fromValidatedData(validatedData);
    }
    /**
     * Convert the ActionItem to a notification event.
     */
    toNotificationEvent() {
        return getNotificationEvent(this);
    }
    /**
     * Convert the ActionItem to a notification for a specific subscriber type.
     */
    toNotification(subscriberType, baseActionUrl) {
        const notificationEvent = getNotificationEvent(this);
        if (!notificationEvent) {
            return null;
        }
        const template = NOTIFICATION_TEMPLATES_BY_EVENT[notificationEvent]?.[subscriberType];
        if (!template) {
            return null;
        }
        // Special handling for risk assessment notifications
        // We only want to send the notification if the risk assessment is approved
        if (notificationEvent === NotificationEvent.REVIEW_RISK_ASSESSMENT_COMPLETE &&
            this.metadata?.result !== RiskReviewStatus.REVIEW_APPROVED) {
            return null;
        }
        // Check if at least one channel is configured
        // Template can have null for channels that aren't used (e.g. Slack-only notifications)
        if (template.email === null &&
            template.sms === null &&
            template.webhook === null &&
            template.slack === null) {
            return null;
        }
        const data = this.metadata
            ? { ...this.metadata, actionUrl: baseActionUrl }
            : { actionUrl: baseActionUrl };
        // Build email payload (use empty values if email is not configured)
        const email = template.email
            ? {
                subject: interpolateTemplate(template.email.subject, data),
                body: interpolateTemplate(template.email.body, data),
            }
            : { subject: '', body: '' };
        // Build SMS payload (use empty string if SMS is not configured)
        const sms = template.sms ? interpolateTemplate(template.sms, data) : '';
        // Build webhook payload (use empty object if webhook is not configured)
        const webhook = template.webhook || {};
        const { schedule } = template;
        return new Notification('', // id will be generated by the repository
        Date.now(), Date.now(), this.id, notificationEvent, email, sms, webhook, schedule, subscriberType, '', // we don't know the subscriber ID yet, will be set by the notification service depending on the subscription
        undefined, // lastNotified
        0, // timesNotified
        false, // scheduleCanceled
        this.metadata);
    }
}
/**
 * Determines the appropriate notification event based on action code and status
 * Explicit by design to get a lint error if we add a new action code without a notification event
 */
function getNotificationEvent(actionItem) {
    switch (actionItem.actionCode) {
        case TarActionItemCode.ASSIGN_VIN:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.ASSIGN_VIN_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.ASSIGN_VIN_COMPLETE;
            }
            break;
        case TarActionItemCode.ASSIGN_ODOMETER_READING:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.ASSIGN_ODOMETER_READING_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.ASSIGN_ODOMETER_READING_COMPLETE;
            }
            break;
        case TarActionItemCode.MARK_PRODUCTION_COMPLETE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_PRODUCTION_COMPLETE_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_PRODUCTION_COMPLETE_COMPLETE;
            }
            break;
        case TarActionItemCode.SUBMIT_OWNER_DETAILS:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_OWNER_DETAILS_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_OWNER_DETAILS_COMPLETE;
            }
            break;
        case TarActionItemCode.ASSIGN_MOTORCYCLE_ENGINE_NUMBER:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.ASSIGN_MOTORCYCLE_ENGINE_NUMBER_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.ASSIGN_MOTORCYCLE_ENGINE_NUMBER_COMPLETE;
            }
            break;
        case TarActionItemCode.CREATE_TAR_TRANSACTION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.CREATE_TAR_TRANSACTION_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.CREATE_TAR_TRANSACTION_COMPLETE;
            }
            break;
        case TarActionItemCode.CREATE_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.CREATE_TAR_PAPERWORK_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.CREATE_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.ESIGN_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.ESIGN_TAR_PAPERWORK_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.ESIGN_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.SCHEDULE_ROBOSIGN_SESSION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SCHEDULE_ROBOSIGN_SESSION_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SCHEDULE_ROBOSIGN_SESSION_COMPLETE;
            }
            break;
        case TarActionItemCode.APPROVE_ROBOSIGN_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.APPROVE_ROBOSIGN_TAR_PAPERWORK_PENDING;
            }
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.APPROVE_ROBOSIGN_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.MAIL_WETSIG_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MAIL_WETSIG_TAR_PAPERWORK_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MAIL_WETSIG_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.RECEIVE_WETSIG_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_WETSIG_TAR_PAPERWORK_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_WETSIG_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.REVIEW_SIGNED_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_SIGNED_TAR_PAPERWORK_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_SIGNED_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.PROCESS_SIGNED_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.PROCESS_SIGNED_TAR_PAPERWORK_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.PROCESS_SIGNED_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.SUBMIT_SIGNED_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_SIGNED_TAR_PAPERWORK_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_SIGNED_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.GENERATE_TEMP_TAG:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.GENERATE_TEMP_TAG_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                // Return Ekho-specific event for Ekho-issued temp tags (includes tracking number)
                if (actionItem.agentId === EKHO_SERVICES_PROVIDER_ID) {
                    return NotificationEvent.EKHO_GENERATE_TEMP_TAG_COMPLETE;
                }
                return NotificationEvent.GENERATE_TEMP_TAG_COMPLETE;
            }
            break;
        case TarActionItemCode.COUNTER_SIGN_TAR_PAPERWORK:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.COUNTER_SIGN_TAR_PAPERWORK_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.COUNTER_SIGN_TAR_PAPERWORK_COMPLETE;
            }
            break;
        case TarActionItemCode.GENERATE_PLATE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.GENERATE_PLATE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.GENERATE_PLATE_COMPLETE;
            }
            break;
        case TarActionItemCode.COMPLETE_WARRANTY_REGISTRATION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.COMPLETE_WARRANTY_REGISTRATION_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.COMPLETE_WARRANTY_REGISTRATION_COMPLETE;
            }
            break;
        case TarActionItemCode.COMPLETE_MSO:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.COMPLETE_MSO_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.COMPLETE_MSO_COMPLETE;
            }
            break;
        case TarActionItemCode.UPLOAD_POST_FULFILLMENT_FORMS:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.UPLOAD_POST_FULFILLMENT_FORMS_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.UPLOAD_POST_FULFILLMENT_FORMS_COMPLETE;
            }
            break;
        case TarActionItemCode.REUPLOAD_POST_FULFILLMENT_FORM:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REUPLOAD_POST_FULFILLMENT_FORM_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REUPLOAD_POST_FULFILLMENT_FORM_COMPLETE;
            }
            break;
        case TarActionItemCode.SHIP_POST_FULFILLMENT_FORMS:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SHIP_POST_FULFILLMENT_FORMS_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SHIP_POST_FULFILLMENT_FORMS_COMPLETE;
            }
            break;
        case TarActionItemCode.SHIP_BUYER_PACKET_TO_SELLER:
            // Only notify selling service provider when completed (packet shipped)
            if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SHIP_BUYER_PACKET_TO_SELLER_COMPLETE;
            }
            break;
        case TarActionItemCode.SHIP_SELLER_FORMS_TO_EKHO:
            // No notifications configured for SHIP_SELLER_FORMS_TO_EKHO yet
            break;
        case TarActionItemCode.VALIDATE_POST_FULFILLMENT_FORM:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.VALIDATE_POST_FULFILLMENT_FORM_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.VALIDATE_POST_FULFILLMENT_FORM_COMPLETE;
            }
            break;
        case TarActionItemCode.BUYER_RON_SESSION:
            // No notifications configured for BUYER_RON_SESSION yet
            break;
        case TarActionItemCode.SELLER_RON_SESSION:
            // No notifications configured for SELLER_RON_SESSION yet
            break;
        // Payment action items
        case PaymentActionItemCode.PAY_IN_FULL:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.PAY_IN_FULL_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.PAY_IN_FULL_COMPLETE;
            }
            break;
        case PaymentActionItemCode.PAY_RESERVATION_FEE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.PAY_RESERVATION_FEE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.PAY_RESERVATION_FEE_COMPLETE;
            }
            break;
        case PaymentActionItemCode.PAY_RESERVATION_BALANCE_EARLY:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.PAY_RESERVATION_BALANCE_EARLY_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.PAY_RESERVATION_BALANCE_EARLY_COMPLETE;
            }
            break;
        case PaymentActionItemCode.PAY_RESERVATION_BALANCE_LATE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.PAY_RESERVATION_BALANCE_LATE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.PAY_RESERVATION_BALANCE_LATE_COMPLETE;
            }
            break;
        case PaymentActionItemCode.RECEIVE_FULL_PAYMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_FULL_PAYMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_FULL_PAYMENT_COMPLETE;
            }
            break;
        case PaymentActionItemCode.RECEIVE_FULL_PAYMENT_ACH:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_FULL_ACH_PAYMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_FULL_ACH_PAYMENT_COMPLETE;
            }
            break;
        case PaymentActionItemCode.RECEIVE_FULL_KLARNA_PAYMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_FULL_KLARNA_PAYMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_FULL_KLARNA_PAYMENT_COMPLETE;
            }
            break;
        case PaymentActionItemCode.RECEIVE_FULL_CRYPTO_PAYMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_FULL_CRYPTO_PAYMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_FULL_CRYPTO_PAYMENT_COMPLETE;
            }
            break;
        case PaymentActionItemCode.RECEIVE_RESERVATION_FEE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_RESERVATION_FEE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_RESERVATION_FEE_COMPLETE;
            }
            break;
        case PaymentActionItemCode.RECEIVE_RESERVATION_BALANCE_EARLY:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_RESERVATION_BALANCE_EARLY_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_RESERVATION_BALANCE_EARLY_COMPLETE;
            }
            break;
        case PaymentActionItemCode.RECEIVE_RESERVATION_BALANCE_LATE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_RESERVATION_BALANCE_LATE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_RESERVATION_BALANCE_LATE_COMPLETE;
            }
            break;
        case PaymentActionItemCode.SIGN_PURCHASE_AGREEMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SIGN_PURCHASE_AGREEMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SIGN_PURCHASE_AGREEMENT_COMPLETE;
            }
            break;
        case PaymentActionItemCode.COUNTER_SIGN_PURCHASE_AGREEMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.COUNTER_SIGN_PURCHASE_AGREEMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.COUNTER_SIGN_PURCHASE_AGREEMENT_COMPLETE;
            }
            break;
        case PaymentActionItemCode.SUBMIT_DISPUTE_EVIDENCE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_DISPUTE_EVIDENCE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_DISPUTE_EVIDENCE_COMPLETE;
            }
            break;
        case PaymentActionItemCode.SIGN_SALE_DOCUMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SIGN_SALE_DOCUMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SIGN_SALE_DOCUMENT_COMPLETE;
            }
            break;
        case PaymentActionItemCode.COUNTER_SIGN_SALE_DOCUMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.COUNTER_SIGN_SALE_DOCUMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.COUNTER_SIGN_SALE_DOCUMENT_COMPLETE;
            }
            break;
        // Security action items
        case SecurityActionItemCode.REVIEW_RISK_ASSESSMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_RISK_ASSESSMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_RISK_ASSESSMENT_COMPLETE;
            }
            break;
        case SecurityActionItemCode.SUBMIT_IDENTITY_VERIFICATION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_IDENTITY_VERIFICATION_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_IDENTITY_VERIFICATION_COMPLETE;
            }
            break;
        case SecurityActionItemCode.REVIEW_IDENTITY_VERIFICATION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_IDENTITY_VERIFICATION_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_IDENTITY_VERIFICATION_COMPLETE;
            }
            break;
        case SecurityActionItemCode.CHECK_OFAC:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.CHECK_OFAC_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.CHECK_OFAC_COMPLETE;
            }
            break;
        // Fulfillment action items
        case FulfillmentActionItemCode.MARK_BACKFILL_INVENTORY_REQUESTED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_BACKFILL_INVENTORY_REQUESTED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_BACKFILL_INVENTORY_REQUESTED_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_RECEIVED_AT_FULFILLMENT_CENTER:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_RECEIVED_AT_FULFILLMENT_CENTER_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_RECEIVED_AT_FULFILLMENT_CENTER_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_BUYER:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_IN_TRANSIT_TO_BUYER_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_IN_TRANSIT_TO_BUYER_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_DELIVERY_FULFILLED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_DELIVERY_FULFILLED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_DELIVERY_FULFILLED_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_PICKUP_FULFILLED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_PICKUP_FULFILLED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_PICKUP_FULFILLED_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_ASSEMBLED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_ASSEMBLED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_ASSEMBLED_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_INSPECTED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_INSPECTED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_INSPECTED_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_PICKUP_SCHEDULED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_PICKUP_SCHEDULED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_PICKUP_SCHEDULED_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_FULFILLED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_FULFILLED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_FULFILLED_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.MARK_BACKFILL_ACCESSORIES_REQUESTED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.MARK_BACKFILL_ACCESSORIES_REQUESTED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.MARK_BACKFILL_ACCESSORIES_REQUESTED_COMPLETE;
            }
            break;
        // Financing action items
        case FinancingActionItemCode.SUBMIT_FINANCING_APPLICATION_TO_EKHO:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_FINANCING_APPLICATION_TO_EKHO_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_FINANCING_APPLICATION_TO_EKHO_COMPLETE;
            }
            break;
        case FinancingActionItemCode.SUBMIT_FINANCING_APPLICATION_TO_LENDING_PARTNERS:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_FINANCING_APPLICATION_TO_LENDING_PARTNERS_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_FINANCING_APPLICATION_TO_LENDING_PARTNERS_COMPLETE;
            }
            break;
        case FinancingActionItemCode.DECLINE_FINANCING_OFFER:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.DECLINE_FINANCING_OFFER_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.DECLINE_FINANCING_OFFER_COMPLETE;
            }
            break;
        case FinancingActionItemCode.SUBMIT_STIPULATION_EVIDENCE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_STIPULATION_EVIDENCE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_STIPULATION_EVIDENCE_COMPLETE;
            }
            break;
        case FinancingActionItemCode.SWITCH_TO_CASH:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SWITCH_TO_CASH_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SWITCH_TO_CASH_COMPLETE;
            }
            break;
        case FinancingActionItemCode.RECEIVE_SWITCH_TO_CASH:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_SWITCH_TO_CASH_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_SWITCH_TO_CASH_COMPLETE;
            }
            break;
        case FinancingActionItemCode.CANCEL_ORDER_AFTER_FINAL_REJECTION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.CANCEL_ORDER_AFTER_FINAL_REJECTION_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.CANCEL_ORDER_AFTER_FINAL_REJECTION_COMPLETE;
            }
            break;
        case FinancingActionItemCode.PAY_DOWN_PAYMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.PAY_DOWN_PAYMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.PAY_DOWN_PAYMENT_COMPLETE;
            }
            break;
        case FinancingActionItemCode.RECEIVE_DOWN_PAYMENT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_DOWN_PAYMENT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_DOWN_PAYMENT_COMPLETE;
            }
            break;
        case FinancingActionItemCode.SIGN_LOAN_CONTRACT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SIGN_LOAN_CONTRACT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SIGN_LOAN_CONTRACT_COMPLETE;
            }
            break;
        case FinancingActionItemCode.SIGN_FINANCING_DOCUMENTS:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SIGN_FINANCING_DOCUMENTS_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SIGN_FINANCING_DOCUMENTS_COMPLETE;
            }
            break;
        case FinancingActionItemCode.VALIDATE_STIPULATION_EVIDENCE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.VALIDATE_STIPULATION_EVIDENCE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.VALIDATE_STIPULATION_EVIDENCE_COMPLETE;
            }
            break;
        case FinancingActionItemCode.COUNTER_SIGN_LOAN_CONTRACT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.COUNTER_SIGN_LOAN_CONTRACT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.COUNTER_SIGN_LOAN_CONTRACT_COMPLETE;
            }
            break;
        case FinancingActionItemCode.SUBMIT_FUNDING_PACKET:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_FUNDING_PACKET_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_FUNDING_PACKET_COMPLETE;
            }
            break;
        case FinancingActionItemCode.RECEIVE_LENDER_FUNDS:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_LENDER_FUNDS_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_LENDER_FUNDS_COMPLETE;
            }
            break;
        case FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_APPROVED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_APPROVED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_APPROVED_COMPLETE;
            }
            break;
        case FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_REJECTED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_REJECTED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_REJECTED_COMPLETE;
            }
            break;
        case FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_STIPS_NEW_COMPLETE;
            }
            break;
        case FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_STIPS_FULLY_CLEARED_COMPLETE;
            }
            break;
        case FinancingActionItemCode.REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_SURFACED_LENDER_DECISION_STIPS_NOT_CLEARED_COMPLETE;
            }
            break;
        case FinancingActionItemCode.FINANCING_OFFER_EXPIRATION_ALERT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.FINANCING_OFFER_EXPIRATION_ALERT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.FINANCING_OFFER_EXPIRATION_ALERT_COMPLETE;
            }
            break;
        // Insurance action items
        case InsuranceActionItemCode.SUBMIT_INSURANCE_INFORMATION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.SUBMIT_INSURANCE_INFORMATION_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.SUBMIT_INSURANCE_INFORMATION_COMPLETE;
            }
            break;
        case InsuranceActionItemCode.REVIEW_INSURANCE_INFORMATION:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.REVIEW_INSURANCE_INFORMATION_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.REVIEW_INSURANCE_INFORMATION_COMPLETE;
            }
            break;
        // Preorder action items
        case PreorderActionItemCode.PAY_DEPOSIT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.PAY_DEPOSIT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.PAY_DEPOSIT_COMPLETE;
            }
            break;
        case PreorderActionItemCode.RECEIVE_DEPOSIT:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.RECEIVE_DEPOSIT_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.RECEIVE_DEPOSIT_COMPLETE;
            }
            break;
        case PreorderActionItemCode.INVITE_TO_CONVERT_TO_FULL_ORDER:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.INVITE_TO_CONVERT_TO_FULL_ORDER_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.INVITE_TO_CONVERT_TO_FULL_ORDER_COMPLETE;
            }
            break;
        case PreorderActionItemCode.CONVERT_TO_FULL_ORDER:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.CONVERT_TO_FULL_ORDER_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.CONVERT_TO_FULL_ORDER_COMPLETE;
            }
            break;
        case FulfillmentActionItemCode.GET_SOFT_LOCK_READY_TO_UPGRADE:
            if (actionItem.status === ActionItemStatus.PENDING) {
                return NotificationEvent.GET_SOFT_LOCK_READY_TO_UPGRADE_PENDING;
            }
            else if (actionItem.status === ActionItemStatus.COMPLETE) {
                return NotificationEvent.GET_SOFT_LOCK_READY_TO_UPGRADE_COMPLETE;
            }
            break;
    }
    return null;
}

export { ActionItem, ActionItemAgent, ActionItemAgentSchema, ActionItemOperation, ActionItemOperationSchema, ActionItemStatus, ActionItemStatusSchema, FinancingActionItemCode, FinancingActionItemCodeSchema, FulfillmentActionItemCode, FulfillmentActionItemCodeSchema, InsuranceActionItemCode, InsuranceActionItemCodeSchema, PaymentActionItemCode, PaymentActionItemCodeSchema, PreorderActionItemCode, PreorderActionItemCodeSchema, SecurityActionItemCode, SecurityActionItemCodeSchema, TarActionItemCode, TarActionItemCodeSchema, actionItemCodeSchema, actionItemCodeValues, actionItemOperationValues, paymentActionItemCodeValues };
//# sourceMappingURL=ActionItem.js.map
